import { useIsScopedOut, useScopedFieldId } from './ScopedFieldContext'

interface FieldGroupProps {
  label: string
  count?: string
  /** Renders `count` in the error tone — the field is past the platform's cap. */
  countOver?: boolean
  /**
   * Actionable, platform-named reason this field cannot publish as written
   * (e.g. "Pinterest won't accept this post: the description is 1,540
   * characters and Pinterest allows 800..."). Rendered under the field.
   */
  error?: string
  sectionId?: string
  highlighted?: boolean
  children: React.ReactNode
}

export function FieldGroup({
  label,
  count,
  countOver,
  error,
  sectionId,
  highlighted,
  children,
}: FieldGroupProps) {
  // When this subtree is scoped to a single field (modal mode), hide every
  // FieldGroup that doesn't match. In normal (unscoped) usage this is a no-op.
  const scopedOut = useIsScopedOut(sectionId)
  const scopedFieldId = useScopedFieldId()
  if (scopedOut) return null

  // Modal scopes to this exact field — the dialog title ("Edit Caption", "Edit
  // Hashtags") already names it, so suppress the inline label to avoid the
  // duplicate. Keep the count (e.g. "170/2200", "Limit reached") since it
  // carries info the title doesn't. The highlight ring (used to mark the
  // active section in the side-by-side editor) is also irrelevant here —
  // there's only one field on screen.
  const isScopedFocus = scopedFieldId !== null && scopedFieldId === sectionId

  return (
    <div
      data-section-id={sectionId}
      className={
        isScopedFocus
          ? ''
          : `rounded-[10px] border px-1 py-0.5 transition-all duration-500 ${
              highlighted ? 'border-acid/40 ring-2 ring-acid/20' : 'border-transparent'
            }`
      }
    >
      {(!isScopedFocus || count) && (
        <div className="mb-1.5 flex items-center justify-between">
          {isScopedFocus ? (
            <span />
          ) : (
            <label className="mono text-[10px] tracking-[0.18em] text-paper-faint uppercase">
              {label}
            </label>
          )}
          {count && (
            <span
              className={`mono text-[10px] ${countOver ? 'font-semibold' : 'text-paper-faint'}`}
              style={countOver ? { color: 'var(--alert-error-text)' } : undefined}
            >
              {count}
            </span>
          )}
        </div>
      )}
      {children}
      {error && (
        <p className="alert mt-1.5 text-[11px]" data-tone="error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
